import React from 'react';
import type { TripStatusApiUsageData } from '../../types/tripStatusApiUsage';
import type { Granularity } from '../../types/timeframe';
import MetricScorecardBar from './components/MetricScorecardBar';
import UsageBarChart from './components/UsageBarChart';
import TripStatusQueriesTable from './components/TripStatusQueriesTable';
import { exportToCsv } from './utils/exportCsv';
import SkeletonLoader from './components/SkeletonLoader';
import styles from './tripStatusApiTab.module.css';

interface TripStatusApiTabProps {
  data: TripStatusApiUsageData | null;
  loading: boolean;
  error: boolean;
  timeframeSubtitle: string;
  dateRangeLabel: string;
  granularity: Granularity;
}

const TripStatusApiTab: React.FC<TripStatusApiTabProps> = ({ data, loading, error, timeframeSubtitle, dateRangeLabel, granularity }) => {
  if (loading) {
    return <SkeletonLoader />;
  }

  if (error) {
    return (
      <div className={styles.stateMessage}>
        <span className={styles.stateMessageTitle}>Something went wrong</span>
        <span className={styles.stateMessageSubtitle}>Please try again.</span>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.stateMessage}>
        <span className={styles.stateMessageTitle}>No data available for the selected range</span>
        <span className={styles.stateMessageSubtitle}>Try adjusting the date range or filters to see results.</span>
      </div>
    );
  }

  const metrics = [
    { label: 'Total Queries', value: data.metrics.totalQueries ?? 0, subtitle: dateRangeLabel },
    { label: 'Average Queries', value: data.metrics.averageQueries ?? 0, subtitle: dateRangeLabel },
    {
      label: 'Max Queries',
      value: data.metrics.maxQueries ?? 0,
      subtitle: dateRangeLabel ,
    },
    {
      label: 'Min Queries',
      value: data.metrics.minQueries ?? 0,
      subtitle: dateRangeLabel ,
    },
  ];

  const handleExport = () => {
    exportToCsv(data.queriesTable as Record<string, any>[], 'trip-status-api-queries.csv');
  };

  return (
    <div className={styles.tab}>
      <MetricScorecardBar metrics={metrics} variant="orange" />
      <UsageBarChart
        title={"Trip Status queries: " + timeframeSubtitle}
        chartData={data.chartData}
        dataLabel="Queries"
        aggregator={granularity}
      />
      <TripStatusQueriesTable data={data.queriesTable} onExport={handleExport} />
    </div>
  );
};

export default TripStatusApiTab;
